import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2Icon, DownloadIcon, CreditCardIcon } from 'lucide-react';
import { usePortalQuery, usePortalMutation } from '../../hooks/usePortalQuery';
import { getInvoice, invoicePdf, listInvoices, listPayments, payInvoice } from '../../lib/portalApi';
import { can } from '../../lib/portalPermissions';
import { LIVE_API } from '../../lib/config';
import { usePortalAuth } from '../../context/PortalAuth';
import { orgName } from '../../context/PortalOrg';
import { Button } from '../../components/ui/Button';
import { PageHeader } from '../../components/portal/PageHeader';
import { StatusBadge } from '../../components/portal/StatusBadge';
import { Dialog } from '../../components/portal/Dialog';
import { EmptyState, InlineError, QueryState } from '../../components/portal/QueryState';
import { formatDate, formatMoney, INVOICE_STATUS_LABELS } from '../../utils/portalFormat';
import type { InvoiceStatus, OrgRole } from '../../types/portal';
const FILTERS: (InvoiceStatus | '')[] = ['', 'overdue', 'sent', 'paid'];
export function PortalInvoices() {
  const {
    orgId = ''
  } = useParams<{
    orgId: string;
  }>();
  const [params, setParams] = useSearchParams();
  const {
    roleIn
  } = usePortalAuth();
  const role = roleIn(orgId) as OrgRole;
  const [status, setStatus] = React.useState<InvoiceStatus | ''>('');
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => listInvoices(orgId, {
    status: status || undefined
  }), [orgId, status]);
  const openId = params.get('invoice');
  const outstanding = (data?.data ?? []).filter(i => i.status === 'sent' || i.status === 'overdue');
  const currency = outstanding[0]?.currency ?? 'USD';
  const totalOutstanding = outstanding.reduce((sum, i) => sum + i.totalCents, 0);
  const ownOnly = !can(role, 'invoices.viewAll');
  return <div data-id="element-2931" data-mp="380w5efs49iek">
      <PageHeader eyebrow={orgName(orgId)} title="Invoices" description={ownOnly ? 'Invoices addressed to you. Owners and admins see every invoice for the organisation.' : 'Every invoice for the organisation.'} data-id="element-2932" data-mp="380mmfc5hobid" />

      <QueryState loading={loading} error={error} data-id="element-2933" data-mp="1bwoo8vjf75q7">
        {data && <>
            {!status && outstanding.length > 0 && <div className="mt-7 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-4" data-id="element-2934" data-mp="1fxs6fooxxdul">
                <p className="font-display text-[34px] font-light text-ink" data-id="element-2935" data-mp="1kyfrltqhn0gw">{formatMoney(totalOutstanding, currency)}</p>
                <p className="text-[15px] text-body" data-id="element-2936" data-mp="1pwhdsnj7nfwl">
                  outstanding across {outstanding.length} invoice{outstanding.length === 1 ? '' : 's'}
                </p>
              </div>}

            <div role="group" aria-label="Filter by status" className="mt-6 flex flex-wrap gap-2" data-id="element-2937" data-mp="1zonceg3ywvkr">
              {FILTERS.map(f => <button key={f || 'all'} type="button" aria-pressed={status === f} onClick={() => setStatus(f)} className={`rounded-full border px-3.5 py-1.5 text-[13px] transition-colors duration-150 ${status === f ? 'border-gold text-ink' : 'border-line text-muted hover:text-ink'}`} data-id="element-2938" data-mp="28nqvvl71rczs">
                  {f ? INVOICE_STATUS_LABELS[f] : 'All'}
                </button>)}
            </div>

            <div className="mt-5" data-id="element-2939" data-mp="29r98hyhwwqm2">
              {data.data.length === 0 ? <EmptyState title={status ? 'No invoices with this status' : 'No invoices yet'} data-id="element-2940" data-mp="36ggqiwjbohl8" /> : <div className="overflow-hidden rounded-md border border-line" data-id="element-2941" data-mp="31hdnfhmwklfw">
                  <table className="w-full text-left text-[14px]" data-id="element-2942" data-mp="2wqotlvnr3cdl">
                    <thead className="hidden bg-panel text-[12px] uppercase tracking-[0.12em] text-muted sm:table-header-group" data-id="element-2943" data-mp="2woucytjii6fb">
                      <tr data-id="element-2944" data-mp="3p6zokamu5aqj">
                        <th scope="col" className="px-5 py-3 font-medium" data-id="element-2945" data-mp="3p89jws2b04th">Invoice</th>
                        <th scope="col" className="px-5 py-3 font-medium" data-id="element-2946" data-mp="3kg9eik3gsjpi">Due</th>
                        <th scope="col" className="px-5 py-3 text-right font-medium" data-id="element-2947" data-mp="34yrqik1wq0dz">Amount</th>
                        <th scope="col" className="px-5 py-3 font-medium" data-id="element-2948" data-mp="39xbculkexe6v">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line" data-id="element-2949" data-mp="2vz4vcfzrtqtd">
                      {data.data.map(invoice => <tr key={invoice.id} className="group relative grid grid-cols-2 gap-1 px-5 py-4 transition-colors duration-150 hover:bg-panel/60 sm:table-row sm:p-0" data-id="element-2950" data-mp="2zzotnmw2z0h3">
                          <td className="sm:px-5 sm:py-4" data-id="element-2951" data-mp="3sn1797k9jxy9">
                            <button type="button" onClick={() => setParams({
                      invoice: invoice.id
                    })} className="text-[15px] text-ink after:absolute after:inset-0 group-hover:text-royal" data-id="element-2952" data-mp="3ewo037f74n9z">
                              {invoice.invoiceNumber}
                            </button>
                            {invoice.notes && <p className="mt-0.5 hidden text-[13px] text-muted sm:block" data-id="element-2953" data-mp="f965q28y4d33">{invoice.notes}</p>}
                          </td>
                          <td className="text-right text-muted sm:px-5 sm:py-4 sm:text-left" data-id="element-2954" data-mp="1zyjppanbiuad">{formatDate(invoice.dueDate)}</td>
                          <td className="text-ink sm:px-5 sm:py-4 sm:text-right" data-id="element-2955" data-mp="1v08f2xp78fr3">{formatMoney(invoice.totalCents, invoice.currency)}</td>
                          <td className="text-right sm:px-5 sm:py-4 sm:text-left" data-id="element-2956" data-mp="24ql8lfezrj9d">
                            <StatusBadge kind="invoice" status={invoice.status} data-id="element-2957" data-mp="24snxfw4er6ci" />
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>

            {can(role, 'payments.view') && <PaymentHistory orgId={orgId} data-id="element-2958" data-mp="1qwk8oicazq7f" />}
          </>}
      </QueryState>

      <InvoiceDetail orgId={orgId} role={role} invoiceId={openId} onClose={() => setParams({})} data-id="element-2959" data-mp="1m1wsd6rge23x" />
    </div>;
}
function InvoiceDetail({
  orgId,
  role,
  invoiceId,
  onClose
}: {
  orgId: string;
  role: OrgRole;
  invoiceId: string | null;
  onClose: () => void;
}) {
  const {
    data,
    loading,
    error
  } = usePortalQuery(() => invoiceId ? getInvoice(orgId, invoiceId) : Promise.resolve(null), [orgId, invoiceId]);
  const pay = usePortalMutation(payInvoice);
  const pdf = usePortalMutation(invoicePdf);
  const [pdfLink, setPdfLink] = React.useState<string | null>(null);
  React.useEffect(() => setPdfLink(null), [invoiceId]);
  const payable = data && (data.status === 'sent' || data.status === 'overdue');
  const onPay = async () => {
    if (!data) return;
    const result = await pay.run(orgId, data.id);
    if (result?.checkoutUrl) window.location.assign(result.checkoutUrl);
  };
  const onPdf = async () => {
    if (!data) return;
    const result = await pdf.run(orgId, data.id);
    if (!result) return;
    if (LIVE_API) window.location.assign(result.url);else setPdfLink(result.url);
  };
  return <Dialog open={!!invoiceId} onClose={onClose} side title={data ? data.invoiceNumber : 'Invoice'} description={data ? `Due ${formatDate(data.dueDate)}` : undefined} data-id="element-2960" data-mp="22z3kybsr4drv">
      <QueryState loading={loading} error={error} rows={3} data-id="element-2961" data-mp="p3vacq1zinjr">
        {data && <div data-id="element-2962" data-mp="l3b8xaadoxrt">
            <div className="flex items-center justify-between" data-id="element-2963" data-mp="l4w677o2uutd">
              <StatusBadge kind="invoice" status={data.status} data-id="element-2964" data-mp="g3ypkzipu8tz" />
              {data.paidAt && <p className="text-[13px] text-muted" data-id="element-2965" data-mp="b5dneujwlf2w">Paid {formatDate(data.paidAt)}</p>}
            </div>

            <table className="mt-6 w-full text-[14px]" data-id="element-2966" data-mp="6624pfzz7zio">
              <caption className="sr-only" data-id="element-2967" data-mp="67vgf9pg59xj">Line items</caption>
              <thead className="border-b border-line text-left text-[12px] uppercase tracking-[0.12em] text-muted" data-id="element-2968" data-mp="1fbzc2rjvwnz">
                <tr data-id="element-2969" data-mp="3tk64s5srdhwe">
                  <th scope="col" className="pb-2 font-medium" data-id="element-2970" data-mp="3tjmmup33ytid">Item</th>
                  <th scope="col" className="pb-2 text-right font-medium" data-id="element-2971" data-mp="3olcoy6q01et2">Qty</th>
                  <th scope="col" className="pb-2 text-right font-medium" data-id="element-2972" data-mp="3jlqf879dqgp0">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-2973" data-mp="3enf1vmjvlo21">
                {data.lines.map(line => <tr key={line.id} data-id="element-2974" data-mp="17c8aq7cdj4m4">
                    <td className="py-3 pr-3 text-body" data-id="element-2975" data-mp="1792l8qzu20pv">{line.description}</td>
                    <td className="py-3 text-right text-muted" data-id="element-2976" data-mp="1bbphr57g89te">{line.quantity}</td>
                    <td className="py-3 text-right text-ink" data-id="element-2977" data-mp="xc7q8i2rqigc">{formatMoney(line.amountCents, data.currency)}</td>
                  </tr>)}
              </tbody>
              <tfoot className="border-t border-line" data-id="element-2978" data-mp="nmo515rl2cib">
                <tr data-id="element-2979" data-mp="nl3ctmsqr5nm">
                  <td colSpan={2} className="pt-3 text-muted" data-id="element-2980" data-mp="sdeeau34qzia">Subtotal</td>
                  <td className="pt-3 text-right text-body" data-id="element-2981" data-mp="sfhcw2icm0ym">{formatMoney(data.subtotalCents, data.currency)}</td>
                </tr>
                <tr data-id="element-2982" data-mp="jkt8eeklknav">
                  <td colSpan={2} className="pt-1 text-muted" data-id="element-2983" data-mp="jmdz1t3sjm3g">Tax</td>
                  <td className="pt-1 text-right text-body" data-id="element-2984" data-mp="4nk0a972r4wg">{formatMoney(data.taxCents, data.currency)}</td>
                </tr>
                <tr data-id="element-2985" data-mp="9m3qilspb2um">
                  <td colSpan={2} className="pt-3 text-[15px] font-medium text-ink" data-id="element-2986" data-mp="9mdek3u6qzxo">Total</td>
                  <td className="pt-3 text-right font-display text-[22px] text-ink" data-id="element-2987" data-mp="3m79bgfkewhlk">{formatMoney(data.totalCents, data.currency)}</td>
                </tr>
              </tfoot>
            </table>

            {data.notes && <p className="mt-6 rounded-sm bg-charcoal px-4 py-3 text-[14px] text-body" data-id="element-2988" data-mp="u8y35byg2e">{data.notes}</p>}

            <div className="mt-8 space-y-3" data-id="element-2989" data-mp="3r7x4t7w45aiy">
              {payable && (can(role, 'invoices.pay') ? <Button className="w-full" disabled={pay.pending} onClick={() => void onPay()} data-id="element-2990" data-mp="4ym5xbku0sdg">
                    <CreditCardIcon className="h-4 w-4" aria-hidden="true" data-id="element-2991" data-mp="iq0vk198vrth" />
                    {pay.pending ? 'Opening secure payment' : `Pay ${formatMoney(data.totalCents, data.currency)}`}
                  </Button> : <p className="text-[14px] text-muted" data-id="element-2992" data-mp="sozxl0zi8p7i">Only an owner can pay invoices for the organisation.</p>)}
              {data.status === 'paid' && pay.pending === false && <p className="flex items-center gap-2 text-[14px] text-[#8BE0AF]" role="status" data-id="element-2993" data-mp="1j6bo74zptjdu">
                  <CheckCircle2Icon className="h-4 w-4" aria-hidden="true" data-id="element-2994" data-mp="1edijui85sd2a" />
                  Paid. A receipt is sent to the owner’s email.
                </p>}
              <Button variant="secondary" className="w-full" disabled={pdf.pending} onClick={() => void onPdf()} data-id="element-2995" data-mp="15ef1jggtg1ou">
                <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-2996" data-mp="223373dbchwhd" />
                {pdf.pending ? 'Preparing PDF' : 'Download PDF'}
              </Button>
              <InlineError error={pay.error ?? pdf.error} data-id="element-2997" data-mp="1x38cozsd2ork" />
              {pdfLink && <p className="break-all text-[12px] text-muted" data-id="element-2998" data-mp="1t1mwb4gmbscm">Preview: a signed PDF link was issued. {pdfLink}</p>}
              {!LIVE_API && payable && can(role, 'invoices.pay') && <p className="text-[12px] text-muted" data-id="element-2999" data-mp="hpvut5dezwyv">In the preview, payment is marked as succeeded straight away. Live, this opens Stripe Checkout.</p>}
            </div>
          </div>}
      </QueryState>
    </Dialog>;
}
function PaymentHistory({
  orgId
}: {
  orgId: string;
}) {
  const {
    data
  } = usePortalQuery(() => listPayments(orgId), [orgId]);
  if (!data || data.data.length === 0) return null;
  return <section aria-labelledby="payments" className="mt-12" data-id="element-3000" data-mp="2xbetu27gd1v4">
      <h2 id="payments" className="font-display text-[20px] text-ink" data-id="element-3001" data-mp="324riudg1naoe">
        Payment history
      </h2>
      <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-3002" data-mp="372j9z58v6srj">
        {data.data.map(p => <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-[14px]" data-id="element-3003" data-mp="3c0kvdwtrj466">
            <span className="text-body" data-id="element-3004" data-mp="3h20c5cowqoir">
              {formatDate(p.paidAt)}, {p.method}
            </span>
            <span className="text-ink" data-id="element-3005" data-mp="3l0pyhurxbgej">{formatMoney(p.amountCents, p.currency)}</span>
          </li>)}
      </ul>
    </section>;
}